import { Link } from "react-router-dom";
import { SITE } from "@/lib/config";
import { truncateAddr } from "@/lib/wallet";
import { CopyButton } from "./CopyButton";
import { LogoLockup } from "./Logo";
import { MonoLabel, XIcon } from "./primitives";

const COLS: { title: string; links: { to: string; label: string }[] }[] = [
  {
    title: "Protocol",
    links: [
      { to: "/protocol", label: "Protocol" },
      { to: "/how-it-works", label: "How It Works" },
      { to: "/ecosystem", label: "Ecosystem" },
      { to: "/docs", label: "Docs" },
    ],
  },
  {
    title: "System",
    links: [
      { to: "/outcomes", label: "Outcomes" },
      { to: "/reputation", label: "Reputation" },
      { to: "/terminal", label: "Terminal" },
      { to: "/manifesto", label: "Manifesto" },
    ],
  },
];

export function Footer() {
  return (
    <footer className="border-t border-line bg-panel">
      <div className="container-t grid gap-12 py-16 md:grid-cols-2 lg:grid-cols-5">
        <div className="lg:col-span-2">
          <Link to="/" data-testid="footer-brand-logo" aria-label="Consequence Protocol — home">
            <LogoLockup />
          </Link>
          <p className="mt-5 max-w-xs text-sm leading-relaxed text-dim">{SITE.positioning}</p>
          <p className="mt-3 max-w-xs font-mono text-xs leading-relaxed text-cyan/80">{SITE.tagline}</p>
          <a
            href={SITE.xUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Consequence Protocol on X"
            data-testid="footer-x-link"
            className="mt-6 inline-flex h-9 w-9 items-center justify-center border border-line text-dim transition-colors duration-200 hover:border-cyan/50 hover:text-cyan"
          >
            <XIcon />
          </a>
        </div>

        {COLS.map((col) => (
          <nav key={col.title} aria-label={col.title} className="flex flex-col gap-3">
            <MonoLabel>{col.title}</MonoLabel>
            {col.links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`footer-link-${l.label.toLowerCase()}`}
                className="font-mono text-xs uppercase tracking-[0.16em] text-dim transition-colors duration-200 hover:text-cyan"
              >
                {l.label}
              </Link>
            ))}
          </nav>
        ))}

        <div className="flex flex-col gap-3">
          <MonoLabel>Token</MonoLabel>
          <span className="font-display text-lg font-bold tracking-wide text-ink">{SITE.ticker}</span>
          <span className="flex items-center gap-2 font-mono text-xs text-dim">
            CA:
            <span className="text-cyan" data-testid="footer-ca-address">
              {truncateAddr(SITE.contractAddress)}
            </span>
            <CopyButton value={SITE.contractAddress} label="Copy" testId="footer-ca-copy-btn" />
          </span>
          <a
            href={SITE.buyUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-buy-link"
            className="font-mono text-xs uppercase tracking-[0.16em] text-dim transition-colors duration-200 hover:text-cyan"
          >
            Buy · {SITE.buyLabel}
          </a>
          <a
            href={SITE.chartUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-chart-link"
            className="font-mono text-xs uppercase tracking-[0.16em] text-dim transition-colors duration-200 hover:text-cyan"
          >
            Chart · {SITE.chartLabel}
          </a>
        </div>
      </div>

      <div className="border-t border-line/60">
        <div className="container-t flex flex-col items-start justify-between gap-2 py-5 sm:flex-row sm:items-center">
          <MonoLabel className="text-[9px]">© 2026 {SITE.projectName}</MonoLabel>
          <MonoLabel className="text-[9px]">Concept interface — all protocol data shown is demo / simulated</MonoLabel>
          <MonoLabel className="text-[9px]">Proof of Consequence</MonoLabel>
        </div>
      </div>
    </footer>
  );
}
